import { blinkConstants } from '@thoughtspot/blink-constants';
import { inIFrame } from '@thoughtspot/browser-util';
import {
    CommunicationConnectionType,
    useGetConnectionsLazyQuery,
} from '@thoughtspot/coms';
import { BLINK_FLAGS } from '@thoughtspot/env-flags';
import { isTrialVersionEnabled } from '@thoughtspot/feature-service';
import { useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import {
    Alert,
    BannerAlertType,
} from '@thoughtspot/radiant-react/widgets/alert';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import {
    getSubscriptionStatus,
    getSubscriptionType,
    onThirdPartyServicesInitiated,
    setPreference,
    SubscriptionStatus,
    SubscriptionType,
    trialDaysLeft,
    useUpdateIsSlackInitiallyConnectedByUser,
} from '/@services/system/config-service/session-service';
import { UserAndDataUsageBannerContainer } from './data-usage-banner/user-and-data-usage-banner.container';
import { TrialInfoContainer } from './trial-info/trial-info.container';

const logger = create('global-banners-container');

const useForceUpdate = () => {
    const [, setState] = React.useState(false);
    return () => {
        setState(state => !state);
    };
};

export const GlobalBanner: React.FC = () => {
    const { sessionService, navService, metricsService } = useAppContext();
    const [isSlackDisconnected, setIsSlackDisconnected] = useState(false);
    const [serviceWorkerBanner, setServiceWorkerBanner] = useState(
        () => (window as any).blink.serviceWorkerBanner,
    );
    const { t } = useTranslation();
    const {
        getCommunicationConnections,
        data,
        loading: getConnectionsLoading,
    } = useGetConnectionsLazyQuery();
    const {
        updateIsSlackInitiallyConnectedByUser,
        error,
        loading,
    } = useUpdateIsSlackInitiallyConnectedByUser();

    const handleSlackConnectionChange = async (
        isSlackConnectedInitially: boolean,
    ) => {
        setPreference(
            blinkConstants.preferenceKeys.IS_SLACK_INITIALLY_CONNECTED,
            isSlackConnectedInitially,
        );
        updateIsSlackInitiallyConnectedByUser(isSlackConnectedInitially);
    };

    useEffect(() => {
        if (sessionService.isComsEnabled()) {
            getCommunicationConnections();
        }
    }, []);

    useEffect(() => {
        if (!getConnectionsLoading) {
            const slackConnection = _.filter(
                data,
                ele => ele.type === CommunicationConnectionType.Slack,
            );
            const wasSlackInitiallyDisconnected = sessionService.isSlackInitiallyConnectedByUser();
            if (
                wasSlackInitiallyDisconnected !== undefined &&
                !wasSlackInitiallyDisconnected &&
                slackConnection.length === 0
            ) {
                setIsSlackDisconnected(true);
            }
            if (
                slackConnection !== undefined &&
                slackConnection.length > 0 &&
                slackConnection[0]?.id !== undefined
            ) {
                setIsSlackDisconnected(false);
            }
        }
    }, [data]);
    const onSlackBannerDisconnectClick = () => {
        handleSlackConnectionChange(true);
        setIsSlackDisconnected(false);
    };
    // Third party services and authenticated view app is loaded parallely,
    // hence its required to rerender this component which evaluates flag value
    // from optimizely when third party services are initialized.
    onThirdPartyServicesInitiated.subscribe(useForceUpdate());

    // Trial Info Banner
    const isExperimentAllowingBanner = () => {
        const experimentFlagValue = sessionService.getValue(
            BLINK_FLAGS.expiryBannerSearchPageExperimentAssets,
        );
        // Trial info banner behind experimentation flag on Search page.
        return !navService?.isCurrentSearchPath() || experimentFlagValue;
    };

    const routes = ['/', '/home', '/insights/home', '/user-preference'];
    const isHomeOrProfilePage = routes.includes(
        navService
            .getUrlHash()
            .replace('#', '')
            .replace('?', ''),
    );
    const isTrialInfoBannerVisible =
        (getSubscriptionType() === SubscriptionType.FREE_TRIAL ||
            getSubscriptionStatus() === SubscriptionStatus.CANCELED ||
            getSubscriptionType() === SubscriptionType.DEVELOPER_EDITION) &&
        isTrialVersionEnabled() &&
        !inIFrame() &&
        trialDaysLeft() >= 0 &&
        isExperimentAllowingBanner;

    // Data row usage banner
    const isDataUsageContainerVisible =
        sessionService.hasAdminPrivileges() &&
        sessionService.getSubscriptionType() ===
            SubscriptionType.TEAMS_EDITION &&
        sessionService.isDataUsageTabEnabled() &&
        !inIFrame() &&
        !sessionService.isCopilotEnabled();

    const isSoftlimitEnabledForTeamsEditionBanner =
        sessionService.userLimitAlertEnabledForTE() &&
        !inIFrame() &&
        !sessionService.isCopilotEnabled();

    if (
        isDataUsageContainerVisible ||
        isSoftlimitEnabledForTeamsEditionBanner
    ) {
        return (
            <UserAndDataUsageBannerContainer
                showUserLimitBanner={isSoftlimitEnabledForTeamsEditionBanner}
                showDataUsageBanner={isDataUsageContainerVisible}
            />
        );
    }
    if (isTrialInfoBannerVisible) {
        return (
            <TrialInfoContainer
                isReactShellEnabled={sessionService.isReactshellEnabled()}
                isEdgeEnabled={sessionService.isEdgeEnabled()}
            />
        );
    }


    if (
        isSlackDisconnected &&
        isHomeOrProfilePage &&
        sessionService.enableSlackDisconnectedBanner()
    ) {
        return (
            <div data-testid="slack-disconnected-banner">
                <Alert.Banner
                    isVisible
                    message={t('SLACK_DISCONNECTED')}
                    showCloseButton
                    type={BannerAlertType.WARNING}
                    global
                    onClose={onSlackBannerDisconnectClick}
                />
            </div>
        );
    }
    return <></>;
};

export default GlobalBanner;
